/**
 * Guardas de rota.
 *
 * Regra que não se negocia: **a surface vem da sessão, não da URL**. Quem é de
 * clínica e digita /admin volta para /app; quem é de plataforma e digita /app
 * vai para /admin. O caminho é conveniência de navegação, nunca permissão.
 *
 * Falta de sessão é redirecionamento (a pessoa se autentica e volta); falta de
 * permissão é **403**, não redirecionamento — mandar alguém para outra tela sem
 * dizer por quê esconde o motivo e faz parecer defeito.
 */

import { redirect } from 'react-router';
import type { LoaderFunctionArgs } from 'react-router';

import { atual, moduloLigado, pode, surfacePermitida } from '@/session/sessao';
import { SURFACES } from './rotas';
import type { Rota } from './rotas';

/** Nome do parâmetro que leva de volta ao destino depois do login. */
export const PARAM_DESTINO = 'destino';

/**
 * Sanitiza o destino recebido por query string.
 *
 * Um `?destino=` é entrada de quem clicou no link, não do produto: sem filtro,
 * `?destino=https://exemplo.invalido` transforma a própria tela de login num
 * trampolim para fora do domínio. Só passa caminho interno — começa com uma
 * barra e não com duas, nem com barra invertida, que o navegador leria como
 * endereço absoluto.
 */
export function destinoSeguro(bruto: string | null | undefined, alternativa = '/app'): string {
    if (!bruto) return alternativa;
    if (!bruto.startsWith('/')) return alternativa;
    if (bruto.startsWith('//') || bruto.startsWith('/\\')) return alternativa;
    return bruto;
}

/** Caminho da surface que a sessão atual pode ver. */
export function baseDaSessao(): string {
    const permitida = surfacePermitida();
    return SURFACES[permitida].basePath;
}

/**
 * Permissão do guarda contra as permissões da sessão.
 *
 * As duas listas ainda falam vocabulários diferentes: `app/routes.js` pede
 * `financeiro.ver` e o papel `financeiro` carrega `financeiro`. Enquanto o
 * backend não publica a lista definitiva, o módulo antes do ponto vale como a
 * permissão inteira — `financeiro.ver` passa para quem tem `financeiro`. É
 * ponte declarada, não coincidência: quando a lista real chegar, some daqui.
 */
export function podeNaRota(permissao: string): boolean {
    if (pode(permissao)) return true;
    const modulo = permissao.split('.')[0];
    return !!modulo && pode(modulo);
}

/** Loader que aplica as guardas declaradas na rota. */
export function loaderDaRota(rota: Rota) {
    return ({ request }: LoaderFunctionArgs) => {
        const url = new URL(request.url);
        const sessao = atual();

        /* Rota de auth com sessão viva: quem já entrou não fica na tela de
           entrar — vai para a surface que o perfil permite. */
        if (rota.surface === 'auth') {
            if (sessao) throw redirect(destinoSeguro(url.searchParams.get(PARAM_DESTINO), baseDaSessao()));
            return null;
        }

        if (rota.guards.includes('session') && !sessao) {
            const destino = `${url.pathname}${url.search}`;
            throw redirect(`/entrar?${PARAM_DESTINO}=${encodeURIComponent(destino)}`);
        }

        /* A autoridade da surface roda antes das permissões: sem isso, um
           usuário de clínica em /admin receberia 403 em vez de voltar para a
           casa dele, e o 403 diria que existe algo ali para ver. */
        const permitida = surfacePermitida(rota.surface);
        if (permitida !== rota.surface) throw redirect(SURFACES[permitida].basePath);

        if (rota.guards.includes('platform') && sessao?.perfil !== 'plataforma') {
            throw redirect(baseDaSessao());
        }

        /* `tenant` exige clínica ativa no contexto. Sessão de plataforma só tem
           clínica durante impersonação, e é assim que ela entra aqui. */
        if (rota.guards.includes('tenant') && !sessao?.clinica) {
            throw redirect(baseDaSessao());
        }

        /* Módulo desligado por feature flag responde 404, e não 403: 403 diria
           "existe e você não pode", quando o certo é "esta clínica não tem".
           Vale só na surface da clínica — flag de módulo é do produto dela. */
        if (rota.surface === 'clinic' && !moduloLigado(rota.id)) {
            throw new Response(`O módulo ${rota.label} não está habilitado para esta clínica.`, {
                status: 404, statusText: 'Módulo não habilitado',
            });
        }

        for (const guarda of rota.guards) {
            if (!guarda.startsWith('perm:')) continue;
            const permissao = guarda.slice('perm:'.length);
            if (!podeNaRota(permissao)) {
                throw new Response(`Sem a permissão ${permissao}.`, { status: 403, statusText: 'Sem permissão' });
            }
        }

        return null;
    };
}
